import React, { FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { useNavigate } from 'react-router'
import { SupplierCollectionsRow, User } from '../../../models'
import { PaginatedTableContract } from '../../common'
import { currencyFormat } from '../../../helpers/currencyCalculation'
import defineAbilityFor from './../defineAbility'
import { getAuthUser } from '../../../helpers'
import { getUser, updateSupplierAssignedUser, toastSuccess } from '../../../api'

interface SupplierCollectionsTableProps extends PaginatedTableContract {
    setSelectedSuppliers: (selectedSuppliers: number[]) => void
    resources: {
        supplierCollections: SupplierCollectionsRow[]
    }
    users: User[]
    selectedSuppliers: number[]
    toggleSupplierSelection: (id: number) => void
}

export const SupplierCollectionsTable = (props: SupplierCollectionsTableProps) => {
    const ability = defineAbilityFor(getAuthUser())
    const { resources, selectedSuppliers, toggleSupplierSelection, users } = props
    const { supplierCollections } = resources

    const canAssign = ability.can('Assign', 'Supplier')
    const canUnassign = ability.can('Unassign', 'Supplier')
    const canViewActions = canAssign || canUnassign
    const canViewAssignedUser = canViewActions

    const [assignedUserNames, setAssignedUserNames] = useState<{
        [key: number]: string | null
    }>({})
    const [showAssignForm, setShowAssignForm] = useState<boolean>(false)
    const [currentSupplierId, setCurrentSupplierId] = useState<number | null>(null)
    const [assignedUserId, setAssignedUserId] = useState<string>('')
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const navigate = useNavigate()

    useEffect(() => {
        let isCancelled = false

        const fetchAssignedUserNames = async () => {
            const userIds = supplierCollections
                .filter((supplierCollection) => supplierCollection.assignedUser)
                .map((supplierCollection) => supplierCollection.assignedUser)
            const uniqueUserIds = Array.from(new Set(userIds))

            try {
                const userPromises = uniqueUserIds.map((id) => getUser(id.toString()))
                const userResponses = await Promise.all(userPromises)

                if (!isCancelled) {
                    const userData = userResponses.reduce(
                        (acc, result) => {
                            if (result.data) {
                                acc[result.data.id] = result.data.name
                            }
                            return acc
                        },
                        {} as { [key: number]: string },
                    )

                    const updatedAssignedUserNames = supplierCollections.reduce(
                        (acc, supplierCollection) => {
                            if (supplierCollection.assignedUser) {
                                acc[supplierCollection.supplierId] =
                                    userData[supplierCollection.assignedUser] || null
                            }
                            return acc
                        },
                        {} as { [key: number]: string | null },
                    )

                    setAssignedUserNames(updatedAssignedUserNames)
                }
            } catch (error) {
                if (!isCancelled) {
                    console.error('Failed to fetch assigned user names:', error)
                }
            }
        }

        fetchAssignedUserNames()

        return () => {
            isCancelled = true
        }
    }, [supplierCollections])

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (assignedUserId) {
            const params = {
                assigned_user_id: assignedUserId,
                selected_suppliers: props.selectedSuppliers,
                reassign: true,
            }
            setIsSubmitting(true)

            updateSupplierAssignedUser(params)
                .then(() => {
                    setIsSubmitting(false)
                    navigate(0)
                    toastSuccess('Suppliers Assigned')
                })
                .catch(() => {
                    setIsSubmitting(false)
                })
        }
    }

    const handleReassignClick = (supplierId: number) => {
        setShowAssignForm(true)
        setCurrentSupplierId(supplierId)
        props.setSelectedSuppliers([supplierId])
    }

    const handleUnassignClick = (supplierId: number) => {
        props.setSelectedSuppliers([supplierId])

        const params = {
            assigned_user_id: '',
            selected_suppliers: [supplierId],
        }

        updateSupplierAssignedUser(params)
            .then(() => {
                navigate(0)
                toastSuccess('Supplier unassigned')
            })
            .catch(() => {
                setIsSubmitting(false)
            })
    }

    const assignableSupplierIds = supplierCollections
        .filter((supplierCollection) => supplierCollection.assignedUser == null)
        .map((supplierCollection) => supplierCollection.supplierId)

    const isAllSelected =
        assignableSupplierIds.length > 0 &&
        assignableSupplierIds.every((id) => selectedSuppliers.includes(id))

    const handleSelectAll = () => {
        if (isAllSelected) {
            props.setSelectedSuppliers(
                selectedSuppliers.filter((id) => !assignableSupplierIds.includes(id)),
            )
        } else {
            props.setSelectedSuppliers(
                Array.from(new Set([...selectedSuppliers, ...assignableSupplierIds])),
            )
        }
    }

    const assignedSuppliersCount = props.users.find(
        (user) => user.id === Number(assignedUserId),
    )?.assignedSupplierCount
    const maximumLimit = assignedSuppliersCount ? assignedSuppliersCount >= 200 : undefined

    return (
        <table className="table w-100" style={{ tableLayout: 'auto' }}>
            <tbody>
                <tr>
                    {canAssign ? (
                        <th>
                            <input
                                className="form-check-input"
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={handleSelectAll}
                                disabled={assignableSupplierIds.length === 0}
                                title="Select all unassigned"
                            />
                        </th>
                    ) : null}
                    <th>Name</th>
                    <th>Substatus</th>
                    <th>Phone</th>
                    <th>Balance</th>
                    <th>Last Updated</th>
                    <th>Flag</th>
                    {canViewAssignedUser && (
                        <>
                            <th>Assigned User</th>
                            {!showAssignForm && <th>Action</th>}
                        </>
                    )}
                </tr>
                {supplierCollections.map((supplierCollection) => (
                    <tr key={supplierCollection.id}>
                        {canAssign ? (
                            <td>
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    disabled={supplierCollection.assignedUser != null}
                                    checked={
                                        selectedSuppliers.includes(
                                            supplierCollection.supplierId,
                                        ) || supplierCollection.assignedUser != null
                                    }
                                    onChange={() =>
                                        toggleSupplierSelection(supplierCollection.supplierId)
                                    }
                                />
                            </td>
                        ) : null}
                        <td className="text-break">
                            <Link to={`/suppliers/${supplierCollection.supplierId}`}>
                                {supplierCollection.supplierName}
                            </Link>
                        </td>
                        <td className="text-break">
                            {supplierCollection.substatus?.name ?? '—'}
                        </td>
                        <td>{supplierCollection.supplierPhone}</td>
                        <td>{currencyFormat(supplierCollection.balance)}</td>
                        <td>{supplierCollection.updated_at.format('MMM DD, YYYY')}</td>
                        <td>
                            <span
                                className={`d-inline-block rounded-circle ${
                                    supplierCollection.hasRecentlyPaid
                                        ? 'bg-success'
                                        : 'bg-danger'
                                }`}
                                style={{ width: '15px', height: '15px' }}
                            ></span>
                        </td>
                        {canViewAssignedUser && (
                            <td>
                                {showAssignForm &&
                                canAssign &&
                                currentSupplierId === supplierCollection.supplierId ? (
                                    <form
                                        onSubmit={handleSubmit}
                                        className="d-flex align-items-center"
                                    >
                                        <div style={{ marginRight: '10px' }}>
                                            <select
                                                className="form-select mb-2"
                                                id="usersInput"
                                                value={assignedUserId}
                                                onChange={(e) =>
                                                    setAssignedUserId(e.target.value)
                                                }
                                                required
                                            >
                                                <option value="">Select User</option>
                                                {users.map((user) => (
                                                    <option
                                                        key={`user_option_${user.id}`}
                                                        value={user.id}
                                                    >
                                                        {user.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            style={{ marginRight: '10px' }}
                                            disabled={isSubmitting}
                                        >
                                            Assign
                                        </button>
                                        <button
                                            className="btn btn-outline-secondary"
                                            onClick={() => {
                                                setShowAssignForm(false)
                                                props.setSelectedSuppliers([])
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </form>
                                ) : (
                                    <span>
                                        {assignedUserNames[supplierCollection.supplierId] ||
                                            'Unassigned'}
                                    </span>
                                )}
                            </td>
                        )}
                        {canViewActions && (
                            <td className="align-middle text-nowrap">
                                {!showAssignForm && (
                                    <div className="d-flex flex-nowrap gap-1 align-items-center">
                                        <button
                                            type="button"
                                            className="btn btn-warning btn-sm"
                                            onClick={() =>
                                                handleReassignClick(
                                                    supplierCollection.supplierId,
                                                )
                                            }
                                            disabled={
                                                supplierCollection.assignedUser == null ||
                                                maximumLimit ||
                                                !canAssign
                                            }
                                        >
                                            Reassign
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm"
                                            disabled={supplierCollection.assignedUser == null ||
                                                !canUnassign
                                            }
                                            onClick={() =>
                                                handleUnassignClick(
                                                    supplierCollection.supplierId,
                                                )
                                            }
                                        >
                                            Unassign
                                        </button>
                                    </div>
                                )}
                            </td>
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    )
}
